import type { HelpMap } from './types';

/** Elementy interfejsu: plansza, nakładki, kolory, panele, karta organizmu. */
export const INTERFACE: HelpMap = {
  'ui.world': {
    group: 'Interfejs',
    title: 'Plansza świata',
    short: 'Kółko myszy = zoom, przeciąganie = przesuwanie, klik w organizm = zaznaczenie. Kółko wokół zaznaczonego to jego zasięg zmysłów, a linia pokazuje, gdzie patrzy.',
    more: [
      'Rozmiar kropki to rozmiar ciała, mała jasna plamka to „nos” — kierunek ruchu. Turkusowe tło to pokarm A, bursztynowe B, szare komórki to bariery; nakładka Nisza pokazuje kultywację.',
      'Renderer rysuje wszystkie organizmy jednym wywołaniem karty graficznej (WebGL2), więc gładko działa nawet przy tysiącach.',
    ],
    try: 'Najedź na planszę, przybliż kółkiem i kliknij pojedynczy organizm.',
    see: ['ui.color', 'ui.overlays'],
  },
  'ui.color': {
    group: 'Interfejs',
    title: 'Tryb koloru',
    short: 'Wybiera, co oznaczają kolory kropek: pokrewieństwo, strategia, energia, pokolenie, wiek, dieta, klad, sygnał albo specjalizacja A/B.',
    more: ['Zmień tryb, żeby zobaczyć inną warstwę tej samej populacji: „Dieta” pokazuje drapieżniki na czerwono, „Klad” — terytoria linii założycieli, „Energia” — kto głoduje.'],
    see: ['color.lineage', 'color.strategy', 'color.energy', 'color.generation', 'color.age', 'color.diet', 'color.clade'],
  },
  'color.lineage': {
    group: 'Interfejs',
    title: 'Kolor: linia',
    short: 'Barwa neutralnego genu „hue”. Spokrewnione organizmy mają podobny kolor, więc widać rodziny.',
    see: ['trait.hue', 'clade'],
  },
  'color.strategy': {
    group: 'Interfejs',
    title: 'Kolor: strategia',
    short: 'Czerwony = prędkość, zielony = zasięg zmysłów, niebieski = rozmiar. Kolor miesza się z tych trzech cech.',
    see: ['strategies'],
  },
  'color.energy': {
    group: 'Interfejs',
    title: 'Kolor: energia',
    short: 'Czerwony = głodny, bursztynowy = średnio, zielony = syty.',
    see: ['energy'],
  },
  'color.generation': {
    group: 'Interfejs',
    title: 'Kolor: pokolenie',
    short: 'Od fioletu/niebieskiego (najstarsze pokolenia) po czerwień (najmłodsze).',
    see: ['generation'],
  },
  'color.age': {
    group: 'Interfejs',
    title: 'Kolor: wiek',
    short: 'Zielony = młody, czerwony = bliski końca życia.',
    see: ['age'],
  },
  'color.diet': {
    group: 'Interfejs',
    title: 'Kolor: dieta',
    short: 'Zielony = roślinożerca, czerwony = drapieżnik, odcienie pośrednie = wszystkożercy.',
    see: ['trait.diet', 'predation'],
  },
  'color.clade': {
    group: 'Interfejs',
    title: 'Kolor: klad',
    short: 'Każdy założyciel ma własny, stały kolor. Widać, czyje potomstwo opanowało który obszar.',
    see: ['clade', 'sweep'],
  },
  'ui.overlays': {
    group: 'Interfejs',
    title: 'Nakładki na planszę',
    short: 'Zasoby — pokarm w tle. Sensory — zasięg wzroku każdego organizmu. Energia — jasny rdzeń proporcjonalny do zapasów. Ślad — trasa zaznaczonego. Gęstość — mapa cieplna zagęszczenia. Podążaj — kamera za zaznaczonym.',
    more: ['Nakładki nie zmieniają symulacji — to tylko sposób patrzenia.'],
    try: 'Włącz „Gęstość” i „Dieta” naraz — czy drapieżniki gromadzą się tam, gdzie ofiary?',
    see: ['ui.world'],
  },
  'overlay.density': {
    group: 'Interfejs',
    title: 'Nakładka: gęstość',
    short: 'Pomarańczowa mapa cieplna: im jaśniej, tym więcej organizmów w danym kwadracie 24×24 jednostek.',
    see: ['ui.overlays'],
  },
  'ui.worlds': {
    group: 'Interfejs',
    title: 'Wiele światów',
    short: 'Każda zakładka u góry to osobny, niezależny świat liczony we własnym wątku. Możesz mieć kilka naraz i porównywać je.',
    more: ['Przyciskiem „+” otwierasz panel nowego eksperymentu. Bliźniak i rozwidlenie dodają kolejne zakładki.'],
    see: ['twin', 'fork', 'compare'],
  },
  'ui.scenario': {
    group: 'Interfejs',
    title: 'Scenariusz startowy',
    short: 'Gotowe zestawy ustawień pokazujące różne zjawiska: drapieżnictwo, płeć, wędrujące plamy, pory roku, ubogie lub obfite środowisko i inne.',
    more: ['Scenariusz zmienia tylko parametry początkowe; wszystko możesz potem regulować na żywo w panelu „Środowisko”.'],
    see: ['predation', 'sexual', 'patches', 'seasons'],
  },
  'ui.founders': {
    group: 'Interfejs',
    title: 'Liczba założycieli',
    short: 'Ile organizmów o losowych genomach zaczyna eksperyment. Z nich pochodzi cała późniejsza zmienność.',
    see: ['founder', 'drift'],
  },
  'ui.patchcount': {
    group: 'Interfejs',
    title: 'Wzorzec i liczba plam',
    short: 'Wzorzec żyzności: plamy (domyślnie), jednolity, gradient (żyźniej po prawej) lub oaza (jedno żyzne miejsce pośrodku). Liczba plam dotyczy wzorca „plamy”.',
    see: ['fertility', 'patches'],
  },
  'ui.autosave': {
    group: 'Interfejs',
    title: 'Autozapis',
    short: 'Co zadaną liczbę ticków świat zapisuje się w pamięci przeglądarki; trzy ostatnie zapisy każdego świata są zachowane.',
    more: ['Przy ponownym wejściu na stronę aplikacja zaproponuje wznowienie. Zapisy są lokalne dla tej przeglądarki i tego adresu strony — nic nie jest wysyłane na serwer.'],
    see: ['save'],
  },
  'ui.learn': {
    group: 'Interfejs',
    title: 'Zakładka „Nauka”',
    short: 'Przewodnik po aplikacji, lekcje z gotowymi eksperymentami i słownik pojęć z ciekawostkami.',
    more: ['Każdy symbol „?” w aplikacji wyjaśnia konkretny element: najedź, żeby zobaczyć skrót, kliknij, aby przypiąć pełny opis. Przycisk „Otwórz w słowniku” przenosi do zakładki Nauka.'],
    see: ['ui.world'],
  },

  // ——— karta organizmu ———
  'insp.energy': {
    group: 'Organizm',
    title: 'Energia organizmu',
    short: 'Aktualny zapas energii przez pojemność. Przy zerze organizm ginie z głodu. Wysoki zapas pozwala się rozmnożyć.',
    see: ['energy'],
  },
  'insp.health': {
    group: 'Organizm',
    title: 'Zdrowie organizmu',
    short: 'Spada ze starością i od zimna, odbudowuje się przy dobrym odżywieniu.',
    see: ['health'],
  },
  'insp.age': {
    group: 'Organizm',
    title: 'Wiek organizmu',
    short: 'Wiek w tickach względem maksymalnego. Po około 60% życia zaczyna się starzenie.',
    see: ['age', 'senescence'],
  },
  'insp.expected': {
    group: 'Organizm',
    title: 'Oczekiwana długość życia',
    short: 'Mniejsza z dwóch wartości: limit wieku oraz wiek, w którym organizm zagłodziłby się, gdyby przestał jeść.',
    see: ['age', 'energy'],
  },
  'insp.starvation': {
    group: 'Organizm',
    title: 'Czas do śmierci głodowej',
    short: 'Ile ticków przetrwałby obecny zapas energii bez żadnego jedzenia, przy bieżących kosztach „bezczynności” (bez ruchu i jedzenia).',
    see: ['metabolism'],
  },
  'insp.costs': {
    group: 'Organizm',
    title: 'Koszty organizmu na tick',
    short: 'Rozbicie kosztu „bezczynności”: metabolizm podstawowy, sensory, mózg i izolacja przed zimnem. Ruch i jedzenie dochodzą do tego dynamicznie.',
    more: ['Koszt sensorów jest ∝ zasięg², koszt mózgu ∝ suma |wag|. Sprawdź, które składniki dominują u różnych organizmów.'],
    see: ['metabolism', 'param.senseCoef', 'param.brainCoef'],
  },
  'insp.mutations': {
    group: 'Organizm',
    title: 'Mutacje względem rodzica',
    short: 'Ile genów różni tego organizmu od jego rodzica. Przy rozmnażaniu płciowym wlicza się też efekt rekombinacji.',
    see: ['mutation', 'sexual'],
  },
  'insp.clade': {
    group: 'Organizm',
    title: 'Klad organizmu',
    short: 'Numer założyciela, z którego pochodzi ta linia.',
    see: ['clade'],
  },
  'insp.memory': {
    group: 'Mózg',
    title: 'Komórki pamięci',
    short: 'Trzy liczby od −1 do 1 przechowywane między tickami. Wracają na wejścia sieci. Turkus = dodatnia, czerwień = ujemna.',
    see: ['memory', 'trait.memory'],
  },
  'insp.genome': {
    group: 'Organizm',
    title: 'Pasek genomu',
    short: 'Każdy prostokąt to jeden gen: najpierw 16 cech, potem 273 wag sieci. Turkus = waga dodatnia, czerwień = ujemna.',
    see: ['genome'],
  },
  'insp.archive': {
    group: 'Organizm',
    title: 'Widok archiwalny (zmarły organizm)',
    short: 'Zmarłe organizmy zostają w genealogii, jeśli mają żywych potomków. Ich genom jest odtwarzany z mutacji przodków.',
    more: ['Dzięki temu możesz zbadać mózg organizmu, który żył tysiące ticków temu — historia nie znika wraz ze śmiercią.'],
    see: ['lineage'],
  },
};
